import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import Device from '@src/config/device'
import Analytics, { CustomEventTypes } from '@src/config/analytics'
import { useImpersonationContext } from '@src/config/impersonation'
import { onboardingSelector, userSelector } from '@src/selectors/app'
import { healthKitSyncStatusSelector } from '@src/selectors/integrations'
import { useDispatchAsync } from '@src/utils/dispatch'
import Storage from '@src/utils/storage'
import { shouldShowModalOnce } from './useOneTimeVisitedState'

export const useSyncHealthKitOnRefresh = (screen: 'home' | 'events') => {
  const dispatchAsync = useDispatchAsync()
  const user = useSelector(userSelector)
  const onboarding = useSelector(onboardingSelector)
  const enabled = useSelector(healthKitSyncStatusSelector)
  const impersonating = useImpersonationContext()

  return useCallback(async () => {
    const reason = [
      { skip: !Device.ios || !Device.hasHealthKit, reason: 'unavailable' },
      { skip: impersonating, reason: 'impersonating' },
      { skip: !user, reason: 'missing_user' },
      { skip: onboarding.isInProgress, reason: 'onboarding' },
      { skip: !enabled, reason: 'sync_disabled' },
      {
        skip: shouldShowModalOnce(Storage.PERMISSIONS_MODAL_VISITED_KEY),
        reason: 'permissions_modal_pending',
      },
    ].find((condition) => condition.skip)?.reason

    Analytics.track(CustomEventTypes.HealthKitSyncRouting, {
      trigger: 'pull_to_refresh',
      screen,
      stage: reason ? 'skipped' : 'native_upload_dispatched',
      ...(reason ? { reason } : {}),
    })
    if (reason) {
      return
    }

    await dispatchAsync({ type: 'app/updateHealthData' })
  }, [dispatchAsync, enabled, impersonating, onboarding.isInProgress, screen, user])
}
